import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { gearResponseSchema, type Assignment } from '@unit/shared';
import { useApi } from '../../lib/useApi.js';
import { ApiError } from '../../components/ApiError.js';
import { formatDate } from '../issuance/FormsPage.js';
import { useAuth } from '../auth/AuthProvider.js';
import { useClaimRoles } from '../auth/useClaimRoles.js';
import { GearActionPanel, availableActions, type GearAction } from './GearActions.js';
import { PdfButton } from '../../components/PdfButton.js';
import { COMMANDER_ROLES, STOREROOM_ROLES } from '../../components/RequireRole.js';

/**
 * A soldier's gear, split into standing and mission gear (REQUIREMENTS §2.5
 * "Missions", §2.6 "my gear split into standing and mission"). Used on the
 * soldier's own home and on a commander's view of the soldier.
 *
 * Each line carries the §2.5 actions its viewer may take — return, expend,
 * report, found, write-off. Which buttons show is decided from the viewer's
 * role, the same way the server decides; whether the action goes through is
 * still decided by the server.
 */
export function GearCard({ uid, title }: { uid: string; title: string }) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { roles } = useClaimRoles();
  const state = useApi(`/users/${uid}/gear`, gearResponseSchema);

  if (state.kind === 'loading') return <p className="text-slate-500">{t('home.loading')}</p>;
  if (state.kind === 'error') return <ApiError code={state.code} requestId={state.requestId} />;

  // Mirrors the server: return / expend / rotate / found are `canIssueTo`
  // (the commander roles — a CHRS reads the file and never touches gear);
  // write-off is the storeroom's. Role-only is enough here because the gear
  // of a soldier outside the viewer's reach is never on screen.
  const viewer = {
    isOwner: user?.uid === uid,
    canManage: roles.some((r) => COMMANDER_ROLES.includes(r)),
    storeroom: roles.some((r) => STOREROOM_ROLES.includes(r)),
  };

  const { assignments, missions } = state.data;
  const standing = assignments.filter((a) => !a.missionId);
  const byMission = new Map<string, Assignment[]>();
  for (const a of assignments) {
    if (!a.missionId) continue;
    byMission.set(a.missionId, [...(byMission.get(a.missionId) ?? []), a]);
  }
  const missionName = (id: string) => missions.find((m) => m.id === id)?.name ?? id;

  return (
    <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200" data-testid="gear-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-semibold text-slate-900">{title}</h2>
        {assignments.length > 0 && <PdfButton path={`/users/${uid}/gear.pdf`} filename="gear.pdf" testId="gear-pdf" />}
      </div>
      {assignments.length === 0 && <p className="mt-2 text-sm text-slate-500">{t('gear.empty')}</p>}

      {standing.length > 0 && (
        <div className="mt-3" data-testid="gear-standing">
          <h3 className="text-xs font-medium uppercase tracking-wide text-slate-500">{t('gear.standing')}</h3>
          <GearList items={standing} language={i18n.language} viewer={viewer} onChanged={state.reload} />
        </div>
      )}
      {[...byMission.entries()].map(([id, items]) => (
        <div key={id} className="mt-4" data-testid={`gear-mission-${id}`}>
          <h3 className="text-xs font-medium uppercase tracking-wide text-amber-700">
            {t('gear.mission', { name: missionName(id) })}
          </h3>
          <GearList items={items} language={i18n.language} viewer={viewer} onChanged={state.reload} />
        </div>
      ))}
    </section>
  );
}

interface Viewer {
  isOwner: boolean;
  canManage: boolean;
  storeroom: boolean;
}

function GearList({
  items,
  language,
  viewer,
  onChanged,
}: {
  items: Assignment[];
  language: string;
  viewer: Viewer;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  // One open panel per list: the action being taken, on which line.
  const [open, setOpen] = useState<{ id: string; action: GearAction } | null>(null);

  return (
    <ul className="mt-1 divide-y divide-slate-100">
      {items.map((a) => {
        const actions = availableActions(a, viewer);
        return (
          <li key={a.id} className="py-2" data-testid="gear-item" data-id={a.id} data-status={a.status}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span>
                <Link to={`/forms/${a.formId}`} className="font-medium text-slate-900 underline-offset-4 hover:underline">
                  {language.startsWith('en') ? a.nameEn : a.nameHe}
                </Link>
                {a.serialNumber ? (
                  <span className="latin-inline ms-2 rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-700">{a.serialNumber}</span>
                ) : (
                  <span className="ms-2 text-sm text-slate-500">× <span className="latin-inline">{a.quantity}</span></span>
                )}
                {a.consumable && a.expendedQuantity > 0 && (
                  <span className="ms-2 text-xs text-slate-500" data-testid="gear-expended">
                    {t('gear.expendedOf', { expended: a.expendedQuantity, total: a.quantity })}
                  </span>
                )}
              </span>
              <span className="flex items-center gap-2 text-xs text-slate-500">
                <StatusPill status={a.status} />
                {a.dueDate && <span className="latin-inline">{formatDate(a.dueDate, language)}</span>}
              </span>
            </div>

            {a.rotationId && (a.status === 'active' || a.status === 'in_rotation' || a.status === 'pending_signature') && (
              <p className="mt-1 text-xs text-amber-700" data-testid="gear-rotation">
                {t('gear.rotationLinked')}
              </p>
            )}

            {a.transferId && a.status === 'active' && !a.attestation && (
              <p className="mt-1 text-xs text-amber-700" data-testid="gear-transfer-pending">
                {t('gear.transferPending')}
              </p>
            )}
            {a.status === 'pending_approval' && (
              <p className="mt-1 text-xs text-amber-700" data-testid="gear-transfer-awaiting">
                {t('gear.transferAwaiting')}
              </p>
            )}
            {a.sourceAssignmentId && (
              <p className="mt-1 text-xs text-sky-800" data-testid="gear-from-store">
                {t('gear.fromPlatoonStore')}
              </p>
            )}
            {a.distributedQuantity > 0 && (
              <p className="mt-1 text-xs text-sky-800" data-testid="gear-distributed">
                {t('gear.distributedOut', { count: a.distributedQuantity })}
              </p>
            )}
            {a.attestation && (
              <p className="mt-1 text-xs text-red-700" data-testid="gear-attested">
                {t('gear.attested', { by: a.attestation.byName })}
              </p>
            )}

            {a.report && !a.report.closedAt && (
              <p className="mt-1 text-xs text-red-700" data-testid="gear-report">
                {t('gear.reportOpen', { kind: t(`gear.${a.report.kind}`) })} · {a.report.notes}
              </p>
            )}

            {actions.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-2">
                {actions.map((action) => (
                  <button
                    key={action}
                    type="button"
                    onClick={() => setOpen(open?.id === a.id && open.action === action ? null : { id: a.id, action })}
                    className={`rounded px-2 py-0.5 text-xs ring-1 transition ${
                      action === 'writeOff' || action === 'report'
                        ? 'text-red-800 ring-red-200 hover:bg-red-50'
                        : 'text-slate-700 ring-slate-300 hover:bg-slate-100'
                    }`}
                    data-testid={`gear-action-${action}`}
                  >
                    {t(`gear.${action}`)}
                  </button>
                ))}
              </div>
            )}

            {open?.id === a.id && (
              <GearActionPanel
                assignment={a}
                action={open.action}
                onDone={() => {
                  setOpen(null);
                  onChanged();
                }}
                onClose={() => setOpen(null)}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
}

export function StatusPill({ status }: { status: Assignment['status'] }) {
  const { t } = useTranslation();
  const tone =
    status === 'active' || status === 'in_rotation'
      ? 'bg-emerald-50 text-emerald-800 ring-emerald-200'
      : status === 'pending_signature' || status === 'pending_approval'
        ? 'bg-amber-50 text-amber-800 ring-amber-200'
        : 'bg-red-50 text-red-800 ring-red-200';
  return <span className={`rounded px-1.5 py-0.5 ring-1 ${tone}`}>{t(`assignmentStatus.${status}`)}</span>;
}
